import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Button, Dialog, Notice, Switch, TextInput } from '@origo/origo-ui';
import { ApiError } from '../../api/client';
import { useCreateBinding, useUpdateBinding } from '../../api/hooks';
import { type BindingUpsertRequest, type SyncBinding, SyncDirectionPolicy } from '../../api/types';
import { toUpsertRequest } from './bindingRequest';
import { DirectionPolicyField } from './DirectionPolicyField';

const BINDING_ID_PATTERN = /^[a-z0-9][a-z0-9-]{0,62}$/;
const FORM_ID = 'binding-form';

interface BindingFormProps {
    open: boolean;
    onClose: () => void;
    /** Når satt → redigeringsmodus (bindingId er låst). */
    existing?: SyncBinding | null;
    defaultSystem: string;
    onSaved?: (binding: SyncBinding) => void;
}

type FormValues = BindingUpsertRequest;

export const BindingForm: React.FC<BindingFormProps> = ({ open, onClose, existing, defaultSystem, onSaved }) => {
    const isEdit = !!existing;
    const createMutation = useCreateBinding();
    const updateMutation = useUpdateBinding();
    const mutation = isEdit ? updateMutation : createMutation;
    const [policyError, setPolicyError] = useState<string | null>(null);
    /** Klar request som venter på bekreftelse av bytte til «Begge veier». */
    const [pendingBoth, setPendingBoth] = useState<BindingUpsertRequest | null>(null);

    const {
        register,
        handleSubmit,
        reset,
        setValue,
        watch,
        formState: { errors },
    } = useForm<FormValues>({
        defaultValues: {
            bindingId: '',
            displayName: '',
            forvaltningssystem: defaultSystem,
            centralDatasetId: '',
            agolBaseUrl: '',
            lokalIdField: 'lokalId',
            enabled: true,
            directionPolicy: null,
        },
    });

    // Synk skjema med valgt kobling når dialogen åpnes.
    useEffect(() => {
        if (!open) return;
        reset(
            existing
                ? {
                      bindingId: existing.bindingId,
                      displayName: existing.displayName,
                      forvaltningssystem: existing.forvaltningssystem,
                      centralDatasetId: existing.centralDatasetId,
                      agolBaseUrl: existing.agolBaseUrl,
                      lokalIdField: existing.lokalIdField,
                      enabled: existing.enabled,
                      // Eldre API uten feltet: vis det som det faktisk betyr (begge veier).
                      directionPolicy: existing.directionPolicy ?? SyncDirectionPolicy.Both,
                  }
                : {
                      bindingId: '',
                      displayName: '',
                      forvaltningssystem: defaultSystem,
                      centralDatasetId: '',
                      agolBaseUrl: '',
                      lokalIdField: 'lokalId',
                      enabled: true,
                      directionPolicy: null,
                  },
        );
        setPolicyError(null);
        setPendingBoth(null);
    }, [open, existing, defaultSystem, reset]);

    const enabled = watch('enabled');
    const directionPolicy = watch('directionPolicy') ?? null;

    const save = async (body: BindingUpsertRequest) => {
        try {
            const saved = isEdit
                ? await updateMutation.mutateAsync({ bindingId: existing!.bindingId, body })
                : await createMutation.mutateAsync(body);
            onSaved?.(saved);
            onClose();
        } catch {
            /* feil vises via mutation.error under */
        }
    };

    const onSubmit = handleSubmit(async (values) => {
        if (values.directionPolicy == null) {
            setPolicyError('Velg synkroniseringsretning.');
            return;
        }
        const simpleFields = {
            ...values,
            lokalIdField: values.lokalIdField?.trim() || 'lokalId',
        };
        // Ved redigering bevarer vi mapping-profilen og editor-feltene fra den eksisterende
        // bindingen — dette skjemaet redigerer kun de enkle feltene. En PUT uten profil ville
        // fått API-et til å generere en fersk profil og overskrive korreksjoner i mappingen.
        // Felt-mappingen endres via mapping-veiviseren.
        const body: BindingUpsertRequest = isEdit
            ? toUpsertRequest(existing!, simpleFields)
            : simpleFields;
        // Å åpne en enveis-kobling for begge veier er det uhellet sperra skal hindre: krev bekreftelse.
        const wasBoth = (existing?.directionPolicy ?? SyncDirectionPolicy.Both) === SyncDirectionPolicy.Both;
        if (isEdit && !wasBoth && values.directionPolicy === SyncDirectionPolicy.Both) {
            setPendingBoth(body);
            return;
        }
        await save(body);
    });

    const serverError = mutation.error instanceof ApiError
        ? mutation.error.message
        : mutation.error
            ? 'Lagring feilet. Sjekk tilkobling og token.'
            : null;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            title={isEdit ? `Rediger kobling: ${existing?.displayName}` : 'Ny kobling'}
            footer={
                <>
                    <Button variant="secondary" onClick={onClose} type="button">
                        Avbryt
                    </Button>
                    <Button variant="primary" type="submit" form={FORM_ID} disabled={mutation.isPending}>
                        {mutation.isPending ? 'Lagrer…' : isEdit ? 'Lagre endringer' : 'Opprett kobling'}
                    </Button>
                </>
            }
        >
            <form id={FORM_ID} onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-04)' }}>
                {serverError && <Notice variant="danger">{serverError}</Notice>}

                {isEdit && (
                    <Notice variant="info">
                        Felt-mapping og verdimap redigeres via mapping-veiviseren, ikke her.
                    </Notice>
                )}

                <TextInput
                    label="Kobling-ID"
                    placeholder="f.eks. bygg-oslo"
                    error={errors.bindingId?.message}
                    helperText="1–63 tegn: små bokstaver, tall og bindestrek. Kan ikke endres senere."
                    disabled={isEdit}
                    {...register('bindingId', {
                        required: 'Kobling-ID er påkrevd.',
                        pattern: {
                            value: BINDING_ID_PATTERN,
                            message: 'Kun små bokstaver, tall og bindestrek; må starte med bokstav/tall.',
                        },
                    })}
                />

                <TextInput
                    label="Visningsnavn"
                    placeholder="Bygg – Oslo kommune"
                    error={errors.displayName?.message}
                    {...register('displayName', { required: 'Visningsnavn er påkrevd.' })}
                />

                <TextInput
                    label="Forvaltningssystem"
                    placeholder="ngis"
                    error={errors.forvaltningssystem?.message}
                    {...register('forvaltningssystem', { required: 'Forvaltningssystem er påkrevd.' })}
                />

                <TextInput
                    label="Sentralt datasett-ID"
                    placeholder="UUID for datasettet i forvaltningssystemet"
                    error={errors.centralDatasetId?.message}
                    {...register('centralDatasetId', { required: 'Datasett-ID er påkrevd.' })}
                />

                <TextInput
                    label="AGOL feature-service URL"
                    placeholder="https://services.arcgis.com/…/FeatureServer"
                    error={errors.agolBaseUrl?.message}
                    helperText="Peker på feature-service-URLen (uten lag-ID)."
                    {...register('agolBaseUrl', { required: 'AGOL-URL er påkrevd.' })}
                />

                <TextInput label="LokalId-felt" placeholder="lokalId" {...register('lokalIdField')} />

                <Switch label="Aktivert" checked={enabled} onChange={(c) => setValue('enabled', c)} />

                <DirectionPolicyField
                    name="binding-form-direction-policy"
                    value={directionPolicy}
                    onChange={(p) => {
                        setValue('directionPolicy', p);
                        setPolicyError(null);
                    }}
                />
                {policyError && <Notice variant="danger">{policyError}</Notice>}
            </form>

            <Dialog
                open={pendingBoth != null}
                onClose={() => setPendingBoth(null)}
                title="Åpne for synk begge veier?"
                footer={
                    <>
                        <Button variant="secondary" onClick={() => setPendingBoth(null)}>
                            Avbryt
                        </Button>
                        <Button
                            variant="danger"
                            disabled={mutation.isPending}
                            onClick={async () => {
                                const body = pendingBoth!;
                                setPendingBoth(null);
                                await save(body);
                            }}
                        >
                            Tillat begge veier
                        </Button>
                    </>
                }
            >
                <p>
                    Koblingen synker i dag bare én vei. Første synk i den nye retningen starter fra null og
                    henter <strong>hele</strong> datasettet fra kilden.
                    {existing?.directionPolicy === SyncDirectionPolicy.AgolToCentralOnly &&
                        ' Dekker det sentrale datasettet et større område (f.eks. et fylke), havner også ' +
                            'objekter utenfor ditt område i AGOL-tjenesten.'}
                </p>
                <p>Sørg for at du har backup før du fortsetter.</p>
            </Dialog>
        </Dialog>
    );
};
